<template>
  <v-navigation-drawer
    :value="isOpenImageGallery"
    :permanent="isOpenImageGallery"
    :width="windowSize.x > 800 ? '800px' : '100vw'"
    max-width="100vw"
    temporary
    fixed
    right
    color="grey lighten-3"
    style="z-index: 17;transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
  >
    <v-card min-height="100vh" color="grey lighten-3">
      <BaseToolbar
        ref="base-toolbar"
        :dark="false"
        @close="close"
        :title="$t('images.gallery.title')"
        type-title="icon"
        color="white"
      >
        <div class="d-flex align-center mr-5">
          <v-btn
            ref="button-import-image"
            @click="importImages()"
            :disabled="!selectedImages.length"
            :loading="importLoading"
            color="primary"
            depressed
            tile
          >
            {{ $t('actions.import') }}
          </v-btn>
        </div>
      </BaseToolbar>
      <v-card-text style="position: relative; overflow-y: auto;height: calc(100vh - 64px)">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          absolute
          top
        />
        <v-container>
          <v-row class="mb-5">
            <v-col
              cols="12"
              class="d-flex align-center"
            >
              <div v-html="$t('images.gallery.text')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="!gallery.length" cols="12">
              <div class="text-center pa-12 my-12">
                <v-img
                  :src="require('@/assets/svg/box_empty.svg')"
                  height="150"
                  max-height="150px"
                  aspect-ratio="1"
                  contain
                />
                <div class="grey--text">
                  {{ $t('common.no_results_found') }}
                </div>
              </div>
            </v-col>
            <v-col
              v-for="(item, index) in gallery"
              :key="index"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card hover outlined>
                <v-img
                  :src="item.url"
                  :lazy-src="item.url"
                  contain
                  height="200"
                  class="grey lighten-2"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular indeterminate color="grey lighten-5" />
                    </v-row>
                  </template>
                </v-img>
                <v-checkbox
                  ref="image-card-checkbox"
                  v-model="selectedImages"
                  :value="item.id"
                  color="primary"
                  hide-details
                  dense
                  small
                  class="ma-0 pa-0"
                  style="position: absolute; top: 12px; left: 12px;"
                />
                <v-card-text style="height: calc(100% - 244px);">
                  <v-row dense>
                    <v-col cols="12" class="text-truncate">
                      {{ item.name }}
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12">
              <CustomPagination
                :pagination="computedPagination"
                @change="getData()"
              />
            </v-col>
          </v-row>
        </v-container>
        <div v-if="totalGallery > gallery.length" v-intersect="onIntersect" class="intersector" />
      </v-card-text>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import BaseToolbar from '@/components/BaseToolbar'
import CustomPagination from '@/components/Common/CustomPagination/Index'

export default {
  name: 'ImageGallery',
  components: {
    BaseToolbar,
    CustomPagination
  },
  data () {
    return {
      loading: false,
      importLoading: false,
      search: '',
      selectedImages: [],
      pagination: {
        page: 1,
        itemsLength: 0,
        itemsPerPage: 12,
        itemsPerPageOptions: [
          { value: 4, text: 4 },
          { value: 8, text: 8 },
          { value: 12, text: 12 },
          { value: 24, text: 24 }
        ],
        sortByOptions: [
          { value: 'name', text: 'Nombre' },
          { value: 'created_at', text: 'Fecha de creación' },
          { value: 'updated_at', text: 'Fecha de actualización' }
        ],
        sortBy: 'name',
        sortDesc: false
      }
    }
  },
  watch: {
    isOpenImageGallery (newVal) {
      if (newVal) {
        this.getData()
      }
    }
  },
  computed: {
    ...mapGetters({
      windowSize: 'windowSize',
      gallery: 'images/gallery',
      totalGallery: 'images/totalGallery',
      isOpenImageGallery: 'images/isOpenImageGallery'
    }),
    computedPagination () {
      const { pagination, totalGallery } = this
      pagination.itemsLength = totalGallery
      return pagination
    }
  },
  created () {
    this.getData()
  },
  methods: {
    ...mapActions({
      getImage: 'images/getImage',
      getGallery: 'images/getGallery',
      cloneImages: 'images/cloneImages'
    }),
    ...mapMutations({
      toggleImageGallery: 'images/toggleImageGallery'
    }),
    handlerInput (arg) {
      if (!arg) { this.close() }
    },
    onIntersect (entries, observer) {
      if (entries[0].isIntersecting && !this.loading) {
        this.pagination.page += 1
        this.getData(true)
      }
    },
    async getData (push) {
      const {
        search,
        computedPagination
      } = this
      this.loading = true
      await this.getGallery({
        search,
        page: computedPagination.page,
        rowsPerPage: computedPagination.itemsPerPage,
        sortBy: computedPagination.sortBy,
        descending: computedPagination.sortDesc,
        push
      })
      this.loading = false
    },
    async importImages () {
      this.importLoading = true
      await this.cloneImages({
        image_ids: this.selectedImages,
        path: 'templates'
      })
      this.close()
    },
    cleanData () {
      this.loading = false
      this.importLoading = false
      this.search = ''
      this.selectedImages = []
      this.pagination = {
        page: 1,
        itemsLength: 0,
        itemsPerPage: 12,
        itemsPerPageOptions: [
          { value: 4, text: 4 },
          { value: 8, text: 8 },
          { value: 12, text: 12 },
          { value: 24, text: 24 }
        ],
        sortByOptions: [
          { value: 'name', text: 'Nombre' },
          { value: 'created_at', text: 'Fecha de creación' },
          { value: 'updated_at', text: 'Fecha de actualización' }
        ],
        sortBy: 'name',
        sortDesc: false
      }
    },
    close () {
      this.toggleImageGallery(false)
      this.cleanData()
    }
  }
}
</script>
